import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import MapPickerModal from './MapPickerModal'
import ReceiptAddModal from './ReceiptAddModal'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'
import {
  calculateAvgSpeedKmh,
  calculateFuelUsedLiters,
  calculateTripDurationMinutes,
  formatDurationHHMM
} from '../helpers/calculateConsumption'

const fields = [
  ['date', 'TRIPLOG.TRIPS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.TRIPS.TABLE.vehicleId'],
  ['receiptId', 'TRIPLOG.TRIPS.TABLE.receiptId'],
  ['spz', 'vehicle_license_plate'],
  ['VIN', 'TRIPLOG.TRIPS.TABLE.VIN'],
  ['driver', 'TRIPLOG.TRIPS.TABLE.driver'],
  ['startTimeIso', 'TRIPLOG.TRIPS.TABLE.startTimeIso'],
  ['startPlace', 'TRIPLOG.TRIPS.TABLE.startPlace'],
  ['endTimeIso', 'TRIPLOG.TRIPS.TABLE.endTimeIso'],
  ['endPlace', 'TRIPLOG.TRIPS.TABLE.endPlace'],
  ['odoStart', 'TRIPLOG.TRIPS.TABLE.odoStart'],
  ['odoEnd', 'TRIPLOG.TRIPS.TABLE.odoEnd'],
  ['distanceKm', 'TRIPLOG.TRIPS.TABLE.distanceKm'],
  ['duration', 'TRIPLOG.TRIPS.TABLE.durationMinutes'],
  ['purpose', 'TRIPLOG.TRIPS.TABLE.purpose'],
  ['avgSpeedKmh', 'TRIPLOG.TRIPS.TABLE.avgSpeedKmh'],
  ['fuelUsed', 'TRIPLOG.TRIPS.TABLE.fuelUsedLiters'],
  ['model', 'TRIPLOG.TRIPS.TABLE.model'],
  ['typ_variant_verzia', 'TRIPLOG.TRIPS.TABLE.typ_variant_verzia'],
  ['note', 'TRIPLOG.TRIPS.TABLE.note'],
]

const PURPOSE_KEYS = [
  'CLIENT_MEETING',
  'DELIVERY',
  'TRAINING',
  'WAREHOUSE_TRANSFER',
  'SERVICE',
  'ADMIN',
  'PRIVATE',
  'OTHER'
]

const REQUIRED_FIELDS = [
  'date',
  'vehicleId',
  'odoStart',
  'odoEnd',
  'startPlace',
  'endPlace',
  'startTimeIso',
  'endTimeIso'
]

const Row = ({ htmlFor, label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
<label
  htmlFor={htmlFor}
  style={{ width: layout.labelW, fontWeight: 700, fontSize: layout.labelFont }}
>
      {label}
    </label>

    <div style={{ display: 'flex', alignItems: 'center', width: layout.inputW }}>
      {children}
    </div>
  </div>
)

const AddTripModal = ({ open, onClose, onSaved, userId }) => {
  const { t } = useTranslation()

const formatForTimeInput = (value) => {

  if (!value) return ''

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''

  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`
}

const getTripSortTime = (trip) => {
  const candidates = [trip?.endTimeIso, trip?.startTimeIso, trip?.date, trip?.createdAt]

  for (const value of candidates) {
    if (!value) continue
    const timestamp = new Date(value).getTime()
    if (Number.isFinite(timestamp)) return timestamp
  }

  return 0
}

const [vw, setVw] = useState(window.innerWidth)

let layout = {}

if (vw >= 1920) {
  layout = {
    labelW: 350,
    inputW: 600,
    inputH: 20,
    labelFont: 15,
    inputFont: 14,
    rowGap: 4
  }
}
else if (vw >= 1366) {
  layout = {
    labelW: 300,
    inputW: 520,
    inputH: 18,
    labelFont: 14,
    inputFont: 13,
    rowGap: 3
  }
}
else {
  layout = {
    labelW: 240,
    inputW: 420,
    inputH: 18,
    labelFont: 13,
    inputFont: 12,
    rowGap: 3
  }
}

const inputStyle = {
  height: layout.inputH,
  fontSize: layout.inputFont,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #999',
  width: '100%'
}
  const [form, setForm] = useState({})
const [mapOpen, setMapOpen] = useState(false)
const [mapField, setMapField] = useState(null)
const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })
const [mapHintTimer, setMapHintTimer] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
const [trips, setTrips] = useState([])
  const [receipts, setReceipts] = useState([])
  const [receiptModalOpen, setReceiptModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  const durationDisplayValue = useMemo(() => {
    const minutes = calculateTripDurationMinutes(
      form.startTimeIso,
      form.endTimeIso
    )

    const formatted = formatDurationHHMM(minutes)
    return formatted || ''
  }, [form.endTimeIso, form.startTimeIso])

  const filteredReceipts = useMemo(() => {
    if (!form.vehicleId) return []

    const assignedReceiptIds = new Set(
      trips
        .map((tripItem) => String(tripItem?.receiptId ?? ''))
        .filter((receiptId) => receiptId)
    )

    return receipts.filter((receipt) => {
      if (String(receipt?.vehicleId) !== String(form.vehicleId)) return false

      const assignedTripId = receipt?.tripId ?? receipt?.trip_id ?? null
      if (assignedTripId) return false

      if (assignedReceiptIds.has(String(receipt?.id))) return false

      return true
    })
  }, [form.vehicleId, receipts, trips])

  const loadVehicles = useCallback(async () => {
    if (!userId) return

    try {
      const res = await fetchWithAuth(`/users/${userId}/vehicles`)
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      const index = data.reduce((acc, v) => {
        if (!v?.id) return acc
        acc[String(v.id)] = v
        return acc
      }, {})

      setVehiclesById(index)
    } catch {}
  }, [userId])

  const loadReceipts = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetchWithAuth(`/users/${userId}/receipts`)
      if (!res.ok) return
      const data = await res.json()
      if (!Array.isArray(data)) return
      setReceipts(data)
    } catch {}
  }, [userId])

const loadTrips = useCallback(async () => {
  if (!userId) return

  try {
    const res = await fetchWithAuth('/triplog/trips')
    if (!res.ok) return

    const data = await res.json()
    if (!Array.isArray(data)) return

    setTrips(data)
  } catch {}
}, [userId])

  useEffect(() => {
    if (!open) return
    if (!open) {
      setMapOpen(false)
      setMapField(null)
      return
    }

    setForm({
      date: '',
      vehicleId: '',
      receiptId: '',
      spz: '',
      VIN: '',
      driver: '',
      startTimeIso: '',
      startPlace: '',
      endTimeIso: '',
      endPlace: '',
      odoStart: '',
      odoEnd: '',
      distanceKm: '',
      duration: '',
      purpose: '',
      avgSpeedKmh: '',
      fuelUsed: '',
      model: '',
      typ_variant_verzia: '',
      note: '',
      mode: 'manual'
    })

    setMessage(null)
    setFieldErrors({})
    loadVehicles()
    loadTrips()
    loadReceipts()
  }, [open, loadVehicles, loadTrips, loadReceipts])

useEffect(() => {
  const handleResize = () => setVw(window.innerWidth)
  window.addEventListener('resize', handleResize)
  return () => window.removeEventListener('resize', handleResize)
}, [])

useEffect(() => {
  if (!open) return

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (receiptModalOpen) {
        return
      }
      if (mapOpen) {
        setMapOpen(false)
        setMapField(null)
        return
      }
      onClose()
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  return () => {
    window.removeEventListener('keydown', handleKeyDown)
  }
}, [open, onClose, mapOpen, receiptModalOpen])

useEffect(() => {
  if (!form.vehicleId || !trips.length) return

  const vehicleTrips = trips
    .filter(t => String(t.vehicleId) === String(form.vehicleId))
    .sort((a, b) => getTripSortTime(b) - getTripSortTime(a))

  const lastTrip = vehicleTrips[0]

  if (!lastTrip?.odoEnd) return

  setForm(prev => ({
    ...prev,
    odoStart: String(lastTrip.odoEnd)
  }))
  }, [form.vehicleId, trips])

  useEffect(() => {
    if (!form.receiptId) return
    const selectedReceipt = receipts.find((receipt) => String(receipt.id) === String(form.receiptId))
    if (selectedReceipt && String(selectedReceipt.vehicleId) !== String(form.vehicleId)) {
      setForm((prev) => ({ ...prev, receiptId: '' }))
    }
  }, [form.vehicleId, form.receiptId, receipts])

useEffect(() => {
  const vehicles = Object.values(vehiclesById)

  if (vehicles.length !== 1) return
  if (form.vehicleId) return

  const vehicle = vehicles[0]

  setForm(prev => ({
    ...prev,
    vehicleId: String(vehicle.id)
  }))
}, [vehiclesById, form.vehicleId])

const validate = () => {
  const newErrors = {}

  REQUIRED_FIELDS.forEach(field => {
    if (!form[field] || String(form[field]).trim() === '') {
      newErrors[field] = true
    }
  })

  setFieldErrors(newErrors)

  if (Object.keys(newErrors).length > 0) {
    setMessage({ message: t('TRIPLOG.FORM.REQUIRED_FIELDS'), type: 'error' })
    return false
  }

  return true
}

const handleMapSelect = (address) => {
  setForm(prev => ({
    ...prev,
    [mapField]: address
  }))
}

  const saveTrip = async () => {
    const odoStart = Number(form.odoStart)
    const odoEnd = Number(form.odoEnd)

if (!validate()) {
  return
}

    if (
      Number.isFinite(odoStart) &&
      Number.isFinite(odoEnd) &&
      odoStart > odoEnd
    ) {
      setFieldErrors({
        odoStart: true,
        odoEnd: true
      })

      setMessage({ message: t('TRIPLOG.ERRORS.ODO_INVALID'), type: 'error' })
      return
    }

    const startTime = form.startTimeIso ? new Date(form.startTimeIso) : null
    const endTime = form.endTimeIso ? new Date(form.endTimeIso) : null

    if (
      startTime &&
      endTime &&
      !isNaN(startTime.getTime()) &&
      !isNaN(endTime.getTime()) &&
      endTime < startTime
    ) {
      setFieldErrors({
        startTimeIso: true,
        endTimeIso: true
      })

      setMessage({ message: t('TRIPLOG.ERRORS.TIME_INVALID'), type: 'error' })
      return
    }

    setFieldErrors({})

    try {
      setLoading(true)

      const computedAvgSpeedKmh = calculateAvgSpeedKmh(form)
      const payload = {
        ...form,
        receiptId: form.receiptId ? Number(form.receiptId) : null,
        avgSpeedKmh: computedAvgSpeedKmh != null ? computedAvgSpeedKmh : null
      }

      const res = await fetchWithAuth('/triplog/trips', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
         const errorPayload = await res.json().catch(() => null)
        const backendError = errorPayload?.error || errorPayload?.message || ''

        if (res.status === 409) {
          setMessage({
            message: t('TRIPLOG.ERRORS.RECEIPT_ALREADY_ASSIGNED', {
              defaultValue: 'Táto účtenka je už priradená k inej jazde.'
            }),
            type: 'error'
          })
          return
        }

        if (typeof backendError === 'string' && backendError.trim()) {
          setMessage({ message: backendError, type: 'error' })
          return
        }

        setMessage({
          message: t('TRIPLOG.ERRORS.SAVE_FAILED', {
            defaultValue: 'Uloženie jazdy zlyhalo.'
          }),
          type: 'error'
        })
        return
      }

      const created = await res.json().catch(() => payload)

      setMessage({ message: t('SAVED'), type: 'success' })
      onSaved?.(created)
      onClose()

    } catch {
      setMessage({
        message: t('TRIPLOG.ERRORS.SAVE_FAILED', {
          defaultValue: 'Uloženie jazdy zlyhalo.'
        }),
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null


  const modalMaxWidth =
    vw >= 2400 ? '28vw' :
    vw >= 1920 ? '40vw' :
    vw >= 1366 ? '45vw' :
    '36vw'

  const modalMaxHeight =
    vw >= 1920 ? '97vh' : '95vh'

  const modalTitleFontSize =
    vw >= 1920 ? 24 :
    vw >= 1366 ? 24 :
    18

const renderField = ([key, labelKey]) => {
  const value = form[key] ?? ''
  
  const vehicle = vehiclesById[String(form.vehicleId)]
    
if (key === 'vehicleId') {
  return (
    <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
<select
  value={form.vehicleId || ''}
  onChange={e => {
    setForm(prev => ({
      ...prev,
      vehicleId: e.target.value || '',
      receiptId: ''
    }))
    setFieldErrors(prev => ({
      ...prev,
      vehicleId: false
    }))
  }}
  style={{
    ...inputStyle,
    height: 34,
    background: !form.vehicleId && fieldErrors.vehicleId ? '#ffdddd' : '#fff'
  }}
>
  <option value="">--</option>
            {Object.values(vehiclesById).map(v => (
              <option key={v.id} value={v.id}>
                {`${v.name || v.model || '-'} (${v.licensePlate || v.spz || '-'})`}
              </option>
            ))}
          </select>
        </Row>
      )
    }

if (key === 'receiptId') {
  return (
    <Row key={key} htmlFor={key} label={t('TRIPLOG.TRIPS.TABLE.receiptId', { defaultValue: labelKey })} layout={layout}>
      <div style={{ display: 'flex', gap: 8, width: '100%' }}>
        <select
          value={form.receiptId || ''}
          onChange={e => setForm(prev => ({ ...prev, receiptId: e.target.value || '' }))}
          style={{ ...inputStyle, height: 34 }}
        >
          <option value="">--</option>
{filteredReceipts.map(receipt => {
  const vehicle = vehiclesById[String(receipt.vehicleId)]

  return (
<option key={receipt.id} value={receipt.id}>
  {receipt.date ? new Date(receipt.date).toLocaleDateString('sk-SK') : '-'} ·
  {vehicle?.name || vehicle?.model || '-'} ·
  {vehicle?.licensePlate || vehicle?.spz || '-'} ·
  {receipt.fuel_name || '-'} ·
  {receipt.units || '-'} {receipt.unit || '-'} ·
  {receipt.total_price || '-'} ·
  {receipt.store_name || '-'}
</option>
  )
})}
        </select>

      </div>
    </Row>
  )
}

    if (key === 'spz') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={vehicle?.licensePlate || vehicle?.spz || ''} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

    if (key === 'VIN') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={vehicle?.vin || ''} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

    if (key === 'model') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={vehicle?.model || ''} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

    if (key === 'typ_variant_verzia') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={vehicle?.variantType || ''} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

    if (key === 'fuelUsed') {
      const liters = calculateFuelUsedLiters(
        form,
        vehicle ? [vehicle] : []
      )

      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={liters != null ? liters.toFixed(2) : ''} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

    if (key === 'avgSpeedKmh') {
      const avgSpeedKmh = calculateAvgSpeedKmh(form)

      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={avgSpeedKmh != null ? avgSpeedKmh.toFixed(1) : ''} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

    if (key === 'mode') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <input value={value || 'manual'} disabled style={{ ...inputStyle, background: '#ddd' }} />
        </Row>
      )
    }

if (key === 'duration') {
  return (
    <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
      <input
        value={durationDisplayValue}
        disabled
        style={{ ...inputStyle, background: '#ddd' }}
      />
    </Row>
  )
}

    if (key === 'purpose') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <select
            value={form.purpose || ''}
            onChange={e => setForm(prev => ({ ...prev, purpose: e.target.value }))}
            style={{ ...inputStyle, height: 34 }}
          >
            {PURPOSE_KEYS.map(k => (
              <option key={k} value={t(`TRIPLOG.PURPOSES.${k}`)}>
                {t(`TRIPLOG.PURPOSES.${k}`)}
              </option>
            ))}
          </select>
        </Row>
      )
    }

    if (key === 'note') {
      return (
        <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
          <textarea
            value={value}
            onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Row>
      )
    }

if (key === 'startPlace' || key === 'endPlace') {
  return (
    <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
      <div style={{ display: 'flex', gap: 6, width: '100%' }}>
        <input
          type="text"
          value={value}
          onChange={e => {
            const val = e.target.value

            setFieldErrors(prev => ({
              ...prev,
              [key]: false
            }))

            setForm(prev => ({
              ...prev,
              [key]: val
            }))
          }}
          style={{
            ...inputStyle,
            background: fieldErrors[key]
              ? '#ffdddd'
              : inputStyle.background
          }}
        />

<button
  type="button"
  onClick={() => {
  setMapField(key)
  setMapOpen(true)
}}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#9ad1bb'

    const timer = setTimeout(() => {
      setShowFullscreenHint(true)
    }, 600)

    setMapHintTimer(timer)
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'

    if (mapHintTimer) {
      clearTimeout(mapHintTimer)
    }

    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
  }}
  style={{
    padding: '0 8px',
    borderRadius: 6,
    border: '1px solid #107c6b',
    background: '#f5f5f5',
    cursor: 'pointer',
    fontWeight: 700,
    boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
    transition: 'transform 0.05s ease, box-shadow 0.1s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={require('../assets/images/icons/map.png')}
    alt=""
    style={{ width: 34, height: 30, pointerEvents: 'none' }}
  />
</button>
      </div>
    </Row>
  )
}

return (
  <Row key={key} htmlFor={key} label={t(labelKey)} layout={layout}>
<input
  type={
    key === 'date'
      ? 'date'
      : (key === 'startTimeIso' || key === 'endTimeIso')
        ? 'time'
        : (key === 'odoStart' || key === 'odoEnd')
          ? 'text'
          : 'text'
  }
      value={
        (key === 'startTimeIso' || key === 'endTimeIso')
          ? formatForTimeInput(value)
          : value
      }
onChange={e => {
  let val = e.target.value

  if (key === 'odoStart' || key === 'odoEnd') {
    // unifikácia čiarka → bodka
    val = val.replace(',', '.')

    // povolené len čísla + max 2 desatinné miesta
    if (!/^\d*\.?\d{0,2}$/.test(val)) {
      return
    }
  }

  setFieldErrors(prev => ({
    ...prev,
    [key]: false
  }))

if (key === 'startTimeIso' || key === 'endTimeIso') {

  const date = form.date ? new Date(form.date) : new Date()

  if (!val) {
    setForm(prev => ({ ...prev, [key]: '' }))
    return
  }

  const [h, m] = val.split(':')
  date.setHours(Number(h))
  date.setMinutes(Number(m))
  date.setSeconds(0)

  setForm(prev => ({
    ...prev,
    [key]: new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      0
    ).toISOString()
  }))
  return
}

        setForm(prev => {
  const updated = { ...prev, [key]: val }

  if (key === 'odoStart' || key === 'odoEnd') {
    const odoStart = Number(updated.odoStart)
    const odoEnd = Number(updated.odoEnd)

    if (
      Number.isFinite(odoStart) &&
      Number.isFinite(odoEnd) &&
      odoEnd >= odoStart
    ) {
      updated.distanceKm = (odoEnd - odoStart).toFixed(2)
    }
  }

  return updated
})
      }}
      onClick={e => {
        if (
          (key === 'date' || key === 'startTimeIso' || key === 'endTimeIso') &&
          e.target.showPicker
        ) {
          e.target.showPicker()
        }
      }}
      disabled={key === 'distanceKm'}
      style={{
        ...inputStyle,
        background:
          fieldErrors[key]
            ? '#ffdddd'
            : key === 'distanceKm'
              ? '#ddd'
              : inputStyle.background
      }}
    />
  </Row>
)
  }

  return (
    <>
      <Overlay open={loading} zIndex={4000} backdropOpacity={0.75} />

<div
  onClick={onClose}
  style={{
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 3100,
    padding: 20
  }}
>
  <div
    onClick={e => e.stopPropagation()}
    style={{
      background: '#ededed',
      padding: '5px 30px 10px 30px',
      border: '2px solid #107c6b',
      borderRadius: 14,
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: modalMaxWidth,
      maxHeight: modalMaxHeight,
      overflow: 'hidden'
    }}
  >
    <h3 style={{ marginTop: 5, marginBottom: 20, fontSize: modalTitleFontSize }}>
      {t('TRIPLOG.ADD')}
    </h3>

          {message && <MessageBox message={message.message} type={message.type} />}

          <div style={{ display: 'flex', flexDirection: 'column', gap: layout.rowGap, overflowY: 'auto' }}>
            {fields.map(renderField)}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 15, justifyContent: 'center' }}>
            <button
              style={modalPrimaryButtonStyle(pressedBtn, 'saveTrip', hoverBtn === 'saveTrip')}
              onMouseEnter={() => setHoverBtn('saveTrip')}
              onMouseLeave={() => { setHoverBtn(null); setPressedBtn(null) }}
              onMouseDown={() => setPressedBtn('saveTrip')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={saveTrip}
            >
              {t('SAVE')}
            </button>

            <button
              style={modalSecondaryButtonStyle(pressedBtn, 'closeTrip', hoverBtn === 'closeTrip')}
              onMouseEnter={() => setHoverBtn('closeTrip')}
              onMouseLeave={() => { setHoverBtn(null); setPressedBtn(null) }}
              onMouseDown={() => setPressedBtn('closeTrip')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('CANCEL')}
            </button>
          </div>
        </div>
      </div>

{showFullscreenHint && (
  <div
    style={{
      position: 'fixed',
      top: cursorPos.y - 50,
      left: cursorPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#d9ffe4',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #107c6b',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {showFullscreenHint === 'receiptNew'
      ? t('TRIPLOG.TRIPS.TABLE.receiptNew')
      : t('TRIPLOG.BUTTON.MAP_PICK')}
  </div>
)}
<MapPickerModal
  open={mapOpen}
  onClose={() => setMapOpen(false)}
  onSelect={handleMapSelect}
/>
<ReceiptAddModal
  open={receiptModalOpen}
  userId={userId}
  onClose={() => {
    setReceiptModalOpen(false)
  }}
  onSaved={(created) => {
    setReceiptModalOpen(false)
    loadReceipts()
    if (created?.id) {
      setForm(prev => ({ ...prev, receiptId: String(created.id) }))
    }
  }}
/>
    </>
  )
}

export default AddTripModal